import { useCallback, useEffect, useRef } from 'react';
import { AreaSeries, CandlestickSeries, createChart, LineStyle } from 'lightweight-charts';
import { areaSeriesOptions, candleSeriesOptions, chartOptions } from '../constants/chartTheme';
import { floorToBucket, timeframeToSeconds } from '../lib/candleAggregation';
const LIVE_RETURN_DELAY_MS = 1500;
const LIVE_RETURN_ANIMATION_MS = 700;
const INITIAL_ZOOM_VISIBLE_BARS = 15;
const INITIAL_ZOOM_ANIMATION_MS = 1000;
const INITIAL_ZOOM_RIGHT_OFFSET = 5;
const INITIAL_ZOOM_TRIGGER_DELAY_MS = 260;
const LIVE_ZOOM_FACTOR_BY_TIMEFRAME = {
  Tick: 0.075,    // was 0.8 — shows only ~30 bars = ultra-tight zoom, line races across screen
  '5s': 0.58,
  '1m': 0.74,
  '5m': 0.72,
  '15m': 0.7,
  '30m': 0.68,
  '1h': 0.66
};
const LIVE_ZOOM_MIN_BARS = 16;        // was 28 — allow zooming in closer
const LIVE_ZOOM_MAX_BARS = 480;
const LIVE_RIGHT_PADDING_BARS = 2;
const BAR_SPACING_BY_TIMEFRAME = {
  Tick: 10,       // smooth, controlled forward movement at ~450ms intervals
  '5s': 24,
  '1m': 14,
  '5m': 12,
  '15m': 11,
  '30m': 10,
  '1h': 9
};
const LIVE_SHIFT_ANIMATION_MS_BY_TIMEFRAME = {
  Tick: 240,      // smoothly glides across ~450ms sub-second ticks without pause or lag
  '5s': 2800,
  '1m': 5000,
  '5m': 6400,
  '15m': 7600,
  '30m': 9000,
  '1h': 10500
};

const CENTERED_LIVE_MIN_BARS = 16;    // allow tighter zoom for mobile & Tick mode
const CENTERED_LIVE_MAX_BARS = 480;
const MIN_PRICE_VALUE = 0.00000001;
const DEFAULT_PRICE_SCALE_LEVELS = 3;  // was 6 — fewer levels = much tighter vertical window
const DYNAMIC_TICK_RANGE_POINTS = 16;   // ~5 seconds of 300ms ticks determine price range
const DYNAMIC_CANDLE_RANGE_POINTS = 72;
const DYNAMIC_RANGE_HEADROOM = 0.55;   // was 1.08 — compress range to 55% of actual = dramatically exaggerated waves
const DYNAMIC_TICK_MIN_SPAN_RATIO = 0.0000008; // was 0.00003 — ~37× tighter vertical span
const DYNAMIC_CANDLE_MIN_SPAN_RATIO = 0.00012;
const DYNAMIC_MAX_SPAN_RATIO = 0.00015; // was 0.0015 — 10× tighter max vertical cap
const DYNAMIC_LOW_QUANTILE = 0.12;
const DYNAMIC_HIGH_QUANTILE = 0.88;
const SMALL_CANDLE_BODY_MIN_PX = 3.2;
const SMALL_CANDLE_BODY_MAX_PX = 8.4;
const SMALL_CANDLE_WICK_MIN_PX = 2.4;
const SMALL_CANDLE_WICK_MAX_PX = 6.4;
const CANDLE_VISUAL_EASING_MS_BY_TIMEFRAME = {
  Tick: 160,
  '5s': 260,
  '1m': 380,
  '5m': 460,
  '15m': 540,
  '30m': 620,
  '1h': 700
};
const LINE_VISUAL_EASING_MS_BY_TIMEFRAME = {
  Tick: 140,
  '5s': 280,
  '1m': 380,
  '5m': 460,
  '15m': 540,
  '30m': 620,
  '1h': 700
};
export const LIVE_PRICE_UP_COLOR = '#00c076';
export const LIVE_PRICE_DOWN_COLOR = '#f6465d';
export const LIVE_PRICE_LABEL_TEXT_COLOR = '#ffffff';
export function formatPriceToSevenDigits(price) {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice)) {
    return '--';
  }

  const absPrice = Math.abs(numericPrice);
  const integerDigits = Math.trunc(absPrice).toString().length;
  const decimals = Math.max(0, 7 - integerDigits);
  const factor = 10 ** decimals;
  const truncatedPrice = Math.trunc(absPrice * factor) / factor;
  const displayPrice = numericPrice < 0 ? -truncatedPrice : truncatedPrice;

  return displayPrice.toFixed(decimals);
}
export function useRealtimeAreaChart({ containerRef, tickSize, timeframe, chartType = 'candle', lineData, candleData, startingPrice, onPriceChange, onLiveMarkerChange, onViewportChange, disableInteractions = false, hideTimeScale = false, disableZoomToLatest = false, fixedZoomPercent = null, keepLatestCentered = true, centeredVisibleBars = null, forwardSpaceBars = LIVE_RIGHT_PADDING_BARS, smoothShiftDurationMs = null, chartBackgroundColor = null, chartFontSize = null, priceScaleStep = null, priceScaleLevels = null, pinPriceScale = true, showLivePriceLine = true, referencePriceLines = [] }) {
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLineRef = useRef(null);
  const referencePriceLinesRef = useRef([]);
  const lastTimeRef = useRef(lineData[lineData.length - 1]?.time ?? Math.floor(Date.now() / 1000));
  const markerTimeRef = useRef(lastTimeRef.current);
  const candleStateRef = useRef(null);
  const realCandleStateRef = useRef(null);
  const onPriceChangeRef = useRef(onPriceChange);
  const onLiveMarkerChangeRef = useRef(onLiveMarkerChange);
  const currentPriceRef = useRef(startingPrice);
  const startingPriceRef = useRef(startingPrice);
  const scaleCenterRef = useRef(startingPrice);
  const autoReturnTimerRef = useRef(null);
  const initialZoomTimerRef = useRef(null);
  const autoZoomRafRef = useRef(null);
  const liveShiftRafRef = useRef(null);
  const candleVisualRafRef = useRef(null);
  const candleVisualLastFrameAtRef = useRef(null);
  const candleVisualTargetRef = useRef(null);
  const lineVisualRafRef = useRef(null);
  const lineVisualLastFrameAtRef = useRef(null);
  const lineVisualTargetRef = useRef(null);
  const lineVisualPointRef = useRef(null);
  const livePriceDirectionRef = useRef('up');
  const isAutoZoomingRef = useRef(false);
  const isAwayFromLiveRef = useRef(false);
  const isLiveShiftAnimatingRef = useRef(false);
  const getTargetLiveRangeRef = useRef(() => null);
  const scheduleLiveReturnRef = useRef(() => { });
  const animateZoomToRecentRef = useRef(() => { });
  const smoothShiftToNextBarRef = useRef(() => { });
  const applyFixedZoomRef = useRef(() => false);
  const applyCenteredRangeRef = useRef(() => false);
  const userCustomVisibleBarsRef = useRef(null);
  const zoomInRef = useRef(() => { });
  const zoomOutRef = useRef(() => { });
  const resetZoomRef = useRef(() => { });
  const onViewportChangeRef = useRef(onViewportChange);
  const pinnedPriceRangeRef = useRef(null);
  const lineDataRef = useRef(lineData);
  const candleDataRef = useRef(candleData);
  const chartTypeRef = useRef(chartType);
  const referencePriceLineDefsRef = useRef(referencePriceLines);
  const totalBarsCountRef = useRef(
    timeframe === 'Tick'
      ? (Array.isArray(lineData) ? lineData.length : 0)
      : (Array.isArray(candleData) ? candleData.length : 0)
  );
  lineDataRef.current = lineData;
  candleDataRef.current = candleData;
  chartTypeRef.current = chartType;
  referencePriceLineDefsRef.current = referencePriceLines;
  useEffect(() => {
    onPriceChangeRef.current = onPriceChange;
  }, [onPriceChange]);
  useEffect(() => {
    onLiveMarkerChangeRef.current = onLiveMarkerChange;
  }, [onLiveMarkerChange]);
  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);
  useEffect(() => {
    if (Number.isFinite(startingPrice)) {
      startingPriceRef.current = startingPrice;
      scaleCenterRef.current = startingPrice;
    }
  }, [startingPrice]);
  const stopCandleVisualAnimation = useCallback(() => {
    if (candleVisualRafRef.current !== null) {
      cancelAnimationFrame(candleVisualRafRef.current);
      candleVisualRafRef.current = null;
    }
    candleVisualLastFrameAtRef.current = null;
  }, []);
  const stopLineVisualAnimation = useCallback(() => {
    if (lineVisualRafRef.current !== null) {
      cancelAnimationFrame(lineVisualRafRef.current);
      lineVisualRafRef.current = null;
    }
    lineVisualLastFrameAtRef.current = null;
  }, []);
  const stepLineVisualAnimation = useCallback((now) => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    const target = lineVisualTargetRef.current;
    if (!series || !target) {
      lineVisualRafRef.current = null;
      lineVisualLastFrameAtRef.current = null;
      return;
    }
    const previous = lineVisualPointRef.current;
    lineVisualLastFrameAtRef.current = now;
    const elapsedMs = now - (target.startedAt ?? now);
    const progress = clamp(elapsedMs / (target.durationMs ?? resolveLineVisualEasingMs(timeframe)), 0, 1);
    // const nextPoint = interpolateLinePoint(previous, target, easeInOutCubic(progress));
    const nextPoint = interpolateLinePoint(previous, target, progress);
    lineVisualPointRef.current = nextPoint;
    series.update(nextPoint);
    priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(nextPoint.value, livePriceDirectionRef.current));
    if (chart) {
      syncLiveMarker(series, chart, nextPoint.value, nextPoint.time, onLiveMarkerChangeRef.current);
    }
    onPriceChangeRef.current?.(nextPoint.value);
    onViewportChangeRef.current?.();
    if (progress >= 1 || areLinePointsVisuallyClose(nextPoint, target)) {
      const finalPoint = normalizeLinePoint(target);
      lineVisualPointRef.current = finalPoint;
      series.update(finalPoint);
      priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(finalPoint.value, livePriceDirectionRef.current));
      if (chart) {
        syncLiveMarker(series, chart, finalPoint.value, finalPoint.time, onLiveMarkerChangeRef.current);
      }
      onPriceChangeRef.current?.(finalPoint.value);
      onViewportChangeRef.current?.();
      lineVisualRafRef.current = null;
      lineVisualLastFrameAtRef.current = null;
      return;
    }
    lineVisualRafRef.current = requestAnimationFrame(stepLineVisualAnimation);
  }, [timeframe]);
  const animateVisualLinePoint = useCallback((targetPoint) => {
    const series = seriesRef.current;
    if (!series || !targetPoint) {
      return;
    }
    const normalizedTarget = normalizeLinePoint(targetPoint);
    const previousPoint = lineVisualPointRef.current;
    lineVisualTargetRef.current = {
      ...normalizedTarget,
      fromValue: Number.isFinite(previousPoint?.value) ? previousPoint.value : normalizedTarget.value,
      startedAt: performance.now(),
      durationMs: resolveLineDrawDurationMs(timeframe, previousPoint, normalizedTarget)
    };
    if (!previousPoint || previousPoint.time !== normalizedTarget.time) {
      const startPoint = buildNewLinePointStart(previousPoint, normalizedTarget);
      lineVisualPointRef.current = startPoint;
      series.update(startPoint);
      priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(startPoint.value, livePriceDirectionRef.current));
      onPriceChangeRef.current?.(startPoint.value);
    }
    if (lineVisualRafRef.current === null) {
      lineVisualLastFrameAtRef.current = performance.now();
      lineVisualRafRef.current = requestAnimationFrame(stepLineVisualAnimation);
    }
  }, [stepLineVisualAnimation]);
  const stepCandleVisualAnimation = useCallback((now) => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    const target = candleVisualTargetRef.current;
    if (!series || !target) {
      candleVisualRafRef.current = null;
      candleVisualLastFrameAtRef.current = null;
      return;
    }
    const previous = candleStateRef.current;
    const lastFrameAt = candleVisualLastFrameAtRef.current ?? now;
    const deltaMs = clamp(now - lastFrameAt, 0, 80);
    candleVisualLastFrameAtRef.current = now;
    const durationMs = resolveCandleVisualEasingMs(timeframe);
    const alpha = 1 - Math.exp(-deltaMs / durationMs);
    const nextCandle = interpolateVisualCandle(previous, target, alpha);
    candleStateRef.current = nextCandle;
    series.update(nextCandle);
    priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(nextCandle.close, livePriceDirectionRef.current));
    if (chart) {
      syncLiveMarker(series, chart, nextCandle.close, nextCandle.time, onLiveMarkerChangeRef.current);
    }
    onPriceChangeRef.current?.(nextCandle.close);
    if (areCandlesVisuallyClose(nextCandle, target)) {
      const finalCandle = normalizeVisualCandle(target);
      candleStateRef.current = finalCandle;
      series.update(finalCandle);
      priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(finalCandle.close, livePriceDirectionRef.current));
      if (chart) {
        syncLiveMarker(series, chart, finalCandle.close, finalCandle.time, onLiveMarkerChangeRef.current);
      }
      onPriceChangeRef.current?.(finalCandle.close);
      candleVisualRafRef.current = null;
      candleVisualLastFrameAtRef.current = null;
      return;
    }
    candleVisualRafRef.current = requestAnimationFrame(stepCandleVisualAnimation);
  }, [timeframe]);
  const animateVisualCandle = useCallback((targetCandle) => {
    const series = seriesRef.current;
    if (!series || !targetCandle) {
      return;
    }
    const normalizedTarget = normalizeVisualCandle(targetCandle);
    const previousVisual = candleStateRef.current;
    candleVisualTargetRef.current = normalizedTarget;
    if (!previousVisual || previousVisual.time !== normalizedTarget.time) {
      const startCandle = buildNewVisualCandleStart(previousVisual, normalizedTarget);
      candleStateRef.current = startCandle;
      series.update(startCandle);
      priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(startCandle.close, livePriceDirectionRef.current));
      onPriceChangeRef.current?.(startCandle.close);
    }
    if (candleVisualRafRef.current === null) {
      candleVisualLastFrameAtRef.current = performance.now();
      candleVisualRafRef.current = requestAnimationFrame(stepCandleVisualAnimation);
    }
  }, [stepCandleVisualAnimation]);
  const syncLiveMarkerToRendered = useCallback(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) {
      return;
    }
    const shouldUseAreaSeries = chartTypeRef.current === 'area' || timeframe === 'Tick';
    let price, time;
    if (shouldUseAreaSeries) {
      if (lineVisualPointRef.current) {
        price = lineVisualPointRef.current.value;
        time = lineVisualPointRef.current.time;
      }
    } else {
      if (candleStateRef.current) {
        price = candleStateRef.current.close;
        time = candleStateRef.current.time;
      }
    }
    if (!Number.isFinite(price)) {
      price = currentPriceRef.current;
      time = markerTimeRef.current;
    }
    syncLiveMarker(series, chart, price, time, onLiveMarkerChangeRef.current);
  }, [timeframe]);
  const syncPinnedPriceRange = useCallback(() => {
    if (!pinPriceScale) {
      pinnedPriceRangeRef.current = null;
      return;
    }
    const current = Number.isFinite(currentPriceRef.current) ? currentPriceRef.current : startingPriceRef.current;
    if (!Number.isFinite(current) || current <= 0) {
      pinnedPriceRangeRef.current = null;
      return;
    }
    const levels = Number.isFinite(priceScaleLevels) && priceScaleLevels > 0
      ? Math.round(priceScaleLevels)
      : DEFAULT_PRICE_SCALE_LEVELS;
    const resolvedStep = (Number.isFinite(priceScaleStep) && priceScaleStep > 0)
      ? priceScaleStep
      : resolveAdaptivePriceScaleStep(current, {
        timeframe,
        lineData: lineDataRef.current,
        candleData: candleDataRef.current,
        levels
      });
    if (!Number.isFinite(resolvedStep) || resolvedStep <= 0) {
      pinnedPriceRangeRef.current = null;
      return;
    }
    const step = Math.max(MIN_PRICE_VALUE, resolvedStep);
    const safeLevels = Math.max(2, levels);
    const halfSpan = (step * safeLevels) / 2;
    const previousCenter = Number.isFinite(scaleCenterRef.current) && scaleCenterRef.current > 0
      ? scaleCenterRef.current
      : current;
    // const nextCenter = previousCenter + ((current - previousCenter) * (timeframe === 'Tick' ? 0.26 : 0.2));
    const nextCenter =
      previousCenter +
      (current - previousCenter) * (timeframe === "Tick" ? 0.95 : 0.2); // was 0.85 — faster Y-axis centering
    scaleCenterRef.current = nextCenter;
    const minValue = Math.max(MIN_PRICE_VALUE, nextCenter - halfSpan);
    const maxValue = Math.max(minValue + MIN_PRICE_VALUE, nextCenter + halfSpan);
    pinnedPriceRangeRef.current = { minValue, maxValue };
  }, [pinPriceScale, priceScaleLevels, priceScaleStep, timeframe]);
  const clearReferencePriceLines = useCallback(() => {
    const series = seriesRef.current;
    if (!series || referencePriceLinesRef.current.length === 0) {
      referencePriceLinesRef.current = [];
      return;
    }
    referencePriceLinesRef.current.forEach((priceLine) => {
      try {
        series.removePriceLine(priceLine);
      }
      catch (_error) {
        // Ignore stale price line handles during teardown.
      }
    });
    referencePriceLinesRef.current = [];
  }, []);
  
  const syncReferencePriceLines = useCallback(() => {
    const series = seriesRef.current;
    if (!series) {
      return;
    }
    clearReferencePriceLines();
    const nextLines = referencePriceLineDefsRef.current
      .filter((line) => Number.isFinite(line?.price))
      .map((line) => series.createPriceLine({
        price: line.price,
        color: line.color ?? 'rgba(255, 217, 57, 0.8)',
        lineWidth: Number.isFinite(line.lineWidth) ? line.lineWidth : 1,
        lineStyle: resolvePriceLineStyle(line.lineStyle),
        axisLabelVisible: line.axisLabelVisible ?? true,
        axisLabelColor: line.axisLabelColor ?? line.color,
        axisLabelTextColor: line.axisLabelTextColor ?? LIVE_PRICE_LABEL_TEXT_COLOR,
        title: line.title ? ` ${line.title} ` : ''
      }));
    referencePriceLinesRef.current = nextLines;
  }, [clearReferencePriceLines]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const chart = createChart(container, {
      ...chartOptions,
      layout: {
        ...chartOptions.layout,
        ...(chartBackgroundColor
          ? { background: { color: chartBackgroundColor } }
          : {}),
        ...(chartFontSize
          ? { fontSize: chartFontSize }
          : {})
      },
      timeScale: {
        ...chartOptions.timeScale,
        barSpacing: BAR_SPACING_BY_TIMEFRAME[timeframe] ?? chartOptions.timeScale.barSpacing,
        shiftVisibleRangeOnNewBar: false,
        // In 5s mode show x-axis labels every 20 seconds (every 4th bar).
        ...(timeframe === '5s'
          ? {
              tickMarkFormatter: (time) => {
                const t = typeof time === 'object' ? 0 : Number(time);
                if (!Number.isFinite(t) || t % 20 !== 0) return null;
                const d = new Date(t * 1000);
                const hh = String(d.getUTCHours()).padStart(2, '0');
                const mm = String(d.getUTCMinutes()).padStart(2, '0');
                const ss = String(d.getUTCSeconds()).padStart(2, '0');
                return `${hh}:${mm}:${ss}`;
              }
            }
          : {}),
        // In Tick mode format UTC time cleanly without skipping.
        ...(timeframe === 'Tick'
          ? {
              tickMarkFormatter: (time) => {
                const t = typeof time === 'object' ? 0 : Number(time);
                if (!Number.isFinite(t)) return '';
                const d = new Date(Math.round(t) * 1000);
                const hh = String(d.getUTCHours()).padStart(2, '0');
                const mm = String(d.getUTCMinutes()).padStart(2, '0');
                const ss = String(d.getUTCSeconds()).padStart(2, '0');
                return `${hh}:${mm}:${ss}`;
              }
            }
          : {}),
        ...(hideTimeScale
          ? {
            visible: false,
            borderVisible: false,
            ticksVisible: false,
            timeVisible: false,
            secondsVisible: false
          }
          : {})
      },

      handleScroll: (disableInteractions || timeframe === 'Tick')
        ? {
          mouseWheel: false,
          pressedMouseMove: false,
          horzTouchDrag: false,
          vertTouchDrag: false
        }
        : chartOptions.handleScroll,
      handleScale: (disableInteractions || timeframe === 'Tick')
        ? {
          pinch: false,
          axisPressedMouseMove: false,
          mouseWheel: false
        }
        : chartOptions.handleScale,
      width: container.clientWidth,
      height: container.clientHeight
    });
    let series;
    let latestTime;
    let latestPrice;
    const withPinnedAutoscale = (baseOptions) => ({
      ...baseOptions,
      autoscaleInfoProvider: (baseImplementation) => {
        const base = typeof baseImplementation === 'function' ? baseImplementation() : null;
        if (!pinPriceScale) {
          return base;
        }
        const pinnedRange = pinnedPriceRangeRef.current;
        if (!pinnedRange) {
          return base;
        }
        return {
          ...(base ?? {}),
          priceRange: pinnedRange
        };
      }
    });
    const shouldUseAreaSeries = chartTypeRef.current === 'area' || timeframe === 'Tick';
    if (shouldUseAreaSeries) {
      const areaData = resolveAreaSeriesData(timeframe, lineData, candleData);
      const lineSeries = chart.addSeries(AreaSeries, withPinnedAutoscale({
        ...areaSeriesOptions,
        priceFormat: {
          type: 'custom',
          formatter: formatPriceToSevenDigits,
          tickmarksFormatter: (prices) => prices.map(formatPriceToSevenDigits),
          minMove: tickSize
        },
        lastValueVisible: false,
        priceLineVisible: false
      }));
      lineSeries.setData(areaData);
      series = lineSeries;
      totalBarsCountRef.current = areaData.length;
      if (timeframe === 'Tick') {
        candleStateRef.current = null;
        realCandleStateRef.current = null;
      }
      else {
        const lastCandle = candleData[candleData.length - 1] ?? null;
        candleStateRef.current = lastCandle;
        realCandleStateRef.current = lastCandle;
      }
      latestTime = areaData[areaData.length - 1]?.time ?? Math.floor(Date.now() / 1000);
      latestPrice = areaData[areaData.length - 1]?.value ?? startingPriceRef.current;
      lineVisualPointRef.current = {
        time: latestTime,
        value: latestPrice
      };
      lineVisualTargetRef.current = lineVisualPointRef.current;
    }
    else {

      const candleSeries = chart.addSeries(CandlestickSeries, withPinnedAutoscale({
        ...candleSeriesOptions,
        priceFormat: {
          type: 'custom',
          formatter: formatPriceToSevenDigits,
          tickmarksFormatter: (prices) => prices.map(formatPriceToSevenDigits),
          minMove: tickSize
        },
        lastValueVisible: false,
        priceLineVisible: false
      }));
      candleSeries.setData(candleData);
      series = candleSeries;
      totalBarsCountRef.current = candleData.length;
      const lastCandle = candleData[candleData.length - 1] ?? null;
      candleStateRef.current = lastCandle;
      realCandleStateRef.current = lastCandle;
      lineVisualPointRef.current = null;
      lineVisualTargetRef.current = null;
      latestTime = lastCandle?.time ?? Math.floor(Date.now() / 1000);
      latestPrice = lastCandle?.close ?? startingPriceRef.current;
    }
    lastTimeRef.current = latestTime;
    markerTimeRef.current = latestTime;
    currentPriceRef.current = latestPrice;
    syncPinnedPriceRange();
    onPriceChangeRef.current?.(latestPrice);
    if (showLivePriceLine) {
      const initialDirection = resolveInitialPriceDirection({
        timeframe,
        chartType: chartTypeRef.current,
        lineData,
        candleData,
        latestPrice
      });
      priceLineRef.current = series.createPriceLine(resolveLivePriceLineOptions(latestPrice, initialDirection));
    }
    else {
      priceLineRef.current = null;
    }
    syncReferencePriceLines();
    const timeScale = chart.timeScale();
    const forceRightPriceAutoScale = () => {
      syncPinnedPriceRange();
      chart.priceScale('right').applyOptions({ autoScale: true });
    };
    const clearAutoReturnTimer = () => {
      if (autoReturnTimerRef.current !== null) {
        clearTimeout(autoReturnTimerRef.current);
        autoReturnTimerRef.current = null;
      }
    };
    const clearInitialZoomTimer = () => {
      if (initialZoomTimerRef.current !== null) {
        clearTimeout(initialZoomTimerRef.current);
        initialZoomTimerRef.current = null;
      }
    };
    const stopAutoZoomAnimation = () => {
      if (autoZoomRafRef.current !== null) {
        cancelAnimationFrame(autoZoomRafRef.current);
        autoZoomRafRef.current = null;
      }
      isAutoZoomingRef.current = false;
    };
    const stopLiveShiftAnimation = () => {
      if (liveShiftRafRef.current !== null) {
        cancelAnimationFrame(liveShiftRafRef.current);
        liveShiftRafRef.current = null;
      }
      isLiveShiftAnimatingRef.current = false;
    };
    const getTargetLiveRange = () => {
      const totalBars = totalBarsCountRef.current > 0
        ? totalBarsCountRef.current
        : (timeframe === 'Tick' ? lineDataRef.current.length : candleDataRef.current.length);
      if (totalBars < 2) {
        return null;
      }
      const latestBarIndex = totalBars - 1;
      const defaultBars = resolveCenteredVisibleWindow(totalBars);

      let visibleBars;
      if (timeframe === 'Tick') {
        const customBars = userCustomVisibleBarsRef.current;
        visibleBars = (Number.isFinite(customBars) && customBars >= defaultBars)
          ? clamp(customBars, defaultBars, CENTERED_LIVE_MAX_BARS)
          : defaultBars;
      } else {
        const currentRange = timeScale.getVisibleLogicalRange();
        const currentWindow = (currentRange && Number.isFinite(currentRange.to - currentRange.from) && (currentRange.to - currentRange.from) > 2)
          ? (currentRange.to - currentRange.from)
          : null;

        visibleBars = (currentWindow && Math.abs(currentWindow - defaultBars) > 4)
          ? currentWindow
          : defaultBars;
      }

      if (keepLatestCentered) {
        const halfWindow = visibleBars / 2;
        return {
          from: latestBarIndex - halfWindow,
          to: latestBarIndex + halfWindow
        };
      }

      const clampedBars = clamp(visibleBars, LIVE_ZOOM_MIN_BARS, LIVE_ZOOM_MAX_BARS);
      const to = latestBarIndex + Math.max(0, forwardSpaceBars);
      const from = to - clampedBars;
      return { from, to };
    };
    const resolveCenteredVisibleWindow = (totalBars) => {
      const containerWidth = container?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 1000);

      if (timeframe === 'Tick') {
        if (containerWidth < 480) {
          // Mobile portrait: show ~58 bars (~26s of 450ms action)
          const mobileBars = Number.isFinite(centeredVisibleBars) ? Math.min(centeredVisibleBars, 64) : 58;
          return clamp(Math.round(mobileBars), CENTERED_LIVE_MIN_BARS, CENTERED_LIVE_MAX_BARS);
        }
        if (containerWidth < 768) {
          // Mobile landscape / tablet: ~76 bars (~34s of 450ms action)
          const tabletBars = Number.isFinite(centeredVisibleBars) ? Math.min(centeredVisibleBars, 80) : 76;
          return clamp(Math.round(tabletBars), CENTERED_LIVE_MIN_BARS, CENTERED_LIVE_MAX_BARS);
        }
        if (Number.isFinite(centeredVisibleBars) && centeredVisibleBars > 2) {
          // Desktop: clamp to ~96-104 bars (~43s of 450ms action)
          const desktopBars = Math.min(centeredVisibleBars, 104);
          return clamp(Math.round(desktopBars), CENTERED_LIVE_MIN_BARS, CENTERED_LIVE_MAX_BARS);
        }
        return clamp(96, CENTERED_LIVE_MIN_BARS, CENTERED_LIVE_MAX_BARS);
      }

      if (Number.isFinite(centeredVisibleBars) && centeredVisibleBars > 2) {
        return clamp(Math.round(centeredVisibleBars), 2, CENTERED_LIVE_MAX_BARS);
      }
      if (Number.isFinite(fixedZoomPercent)) {
        const normalizedPercent = clamp(fixedZoomPercent, 0.05, 1);
        return clamp(Math.round(totalBars * normalizedPercent), CENTERED_LIVE_MIN_BARS, CENTERED_LIVE_MAX_BARS);
      }
      const zoomFactor = LIVE_ZOOM_FACTOR_BY_TIMEFRAME[timeframe] ?? 0.86;
      return clamp(Math.round(totalBars * zoomFactor), CENTERED_LIVE_MIN_BARS, CENTERED_LIVE_MAX_BARS);
    };
    const applyCenteredRange = () => {
      if (!keepLatestCentered) {
        return false;
      }
      const target = getTargetLiveRange();
      if (!target) {
        return false;
      }
      stopLiveShiftAnimation();
      timeScale.setVisibleLogicalRange(target);
      forceRightPriceAutoScale();
      isAwayFromLiveRef.current = false;
      return true;
    };
    const animateToLiveRange = () => {
      const targetRange = getTargetLiveRange();
      if (!targetRange) {
        return;
      }
      const currentRange = timeScale.getVisibleLogicalRange();
      if (!currentRange) {
        timeScale.setVisibleLogicalRange(targetRange);
        isAwayFromLiveRef.current = false;
        return;
      }
      stopAutoZoomAnimation();
      stopLiveShiftAnimation();
      const startFrom = currentRange.from;
      const startTo = currentRange.to;
      const startedAt = performance.now();
      isAutoZoomingRef.current = true;
      const step = (now) => {
        const progress = clamp((now - startedAt) / LIVE_RETURN_ANIMATION_MS, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const liveTarget = getTargetLiveRange() ?? targetRange;
        timeScale.setVisibleLogicalRange({
          from: startFrom + (liveTarget.from - startFrom) * eased,
          to: startTo + (liveTarget.to - startTo) * eased
        });
        forceRightPriceAutoScale();
        syncLiveMarkerToRendered();
        onViewportChangeRef.current?.();
        if (progress >= 1) {
          autoZoomRafRef.current = null;
          isAutoZoomingRef.current = false;
          isAwayFromLiveRef.current = false;
          return;
        }
        autoZoomRafRef.current = requestAnimationFrame(step);
      };
      autoZoomRafRef.current = requestAnimationFrame(step);
    };
    const scheduleLiveReturn = (delayMs = LIVE_RETURN_DELAY_MS) => {
      clearAutoReturnTimer();
      autoReturnTimerRef.current = setTimeout(() => {
        animateToLiveRange();
      }, delayMs);
    };
    const animateZoomToRecentBars = () => {
      const totalBars = timeframe === 'Tick'
        ? lineDataRef.current.length
        : candleDataRef.current.length;
      if (totalBars < 2) {
        return;
      }
      const timeScaleRange = timeScale.getVisibleLogicalRange();
      const endFrom = Math.max(0, totalBars - INITIAL_ZOOM_VISIBLE_BARS);
      const endTo = totalBars;
      if (!timeScaleRange) {
        timeScale.setVisibleLogicalRange({ from: endFrom, to: endTo });
        forceRightPriceAutoScale();
        timeScale.applyOptions({ rightOffset: INITIAL_ZOOM_RIGHT_OFFSET });
        isAwayFromLiveRef.current = false;
        return;
      }
      stopAutoZoomAnimation();
      const startedAt = performance.now();
      isAutoZoomingRef.current = true;
      const step = (now) => {
        const progress = clamp((now - startedAt) / INITIAL_ZOOM_ANIMATION_MS, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        timeScale.setVisibleLogicalRange({
          from: timeScaleRange.from + (endFrom - timeScaleRange.from) * eased,
          to: timeScaleRange.to + (endTo - timeScaleRange.to) * eased
        });
        forceRightPriceAutoScale();
        syncLiveMarkerToRendered();
        onViewportChangeRef.current?.();
        if (progress >= 1) {
          autoZoomRafRef.current = null;
          isAutoZoomingRef.current = false;
          isAwayFromLiveRef.current = false;
          timeScale.applyOptions({ rightOffset: INITIAL_ZOOM_RIGHT_OFFSET });
          return;
        }
        autoZoomRafRef.current = requestAnimationFrame(step);
      };
      autoZoomRafRef.current = requestAnimationFrame(step);
    };
    const applyFixedZoomRange = () => {
      if (!Number.isFinite(fixedZoomPercent)) {
        return false;
      }
      const totalBars = timeframe === 'Tick'
        ? lineDataRef.current.length
        : candleDataRef.current.length;
      if (totalBars < 2) {
        return false;
      }
      const normalizedPercent = clamp(fixedZoomPercent, 0.05, 1);
      const visibleBars = clamp(Math.round(totalBars * normalizedPercent), 2, totalBars);
      const to = totalBars - 1 + Math.max(0, forwardSpaceBars);
      const from = to - visibleBars;
      timeScale.setVisibleLogicalRange({ from, to });
      forceRightPriceAutoScale();
      isAwayFromLiveRef.current = false;
      return true;
    };
    const smoothShiftToNextBar = () => {
      if (isAwayFromLiveRef.current) {
        return;
      }
      clearAutoReturnTimer();
      const target = getTargetLiveRange();
      if (!target) {
        return;
      }
      const currentRange = timeScale.getVisibleLogicalRange();
      if (!currentRange) {
        timeScale.setVisibleLogicalRange(target);
        forceRightPriceAutoScale();
        syncLiveMarkerToRendered();
        onViewportChangeRef.current?.();
        return;
      }

      const diff = target.to - currentRange.to;
      // If the chart has drifted (e.g. > 2.5 bars behind or ahead), immediately snap back to center
      if (Math.abs(diff) > 2.5) {
        stopLiveShiftAnimation();
        timeScale.setVisibleLogicalRange(target);
        forceRightPriceAutoScale();
        syncLiveMarkerToRendered();
        onViewportChangeRef.current?.();
        return;
      }

      if (Math.abs(diff) < 0.01) {
        return;
      }

      const fromStart = currentRange.from;
      const toStart = currentRange.to;
      const fromTarget = target.from;
      const toTarget = target.to;
      const durationMs = Number.isFinite(smoothShiftDurationMs)
        ? Math.max(60, Math.trunc(smoothShiftDurationMs))
        : (timeframe === 'Tick' ? 240 : (LIVE_SHIFT_ANIMATION_MS_BY_TIMEFRAME[timeframe] ?? 560));

      const startedAt = performance.now();
      stopLiveShiftAnimation();
      isLiveShiftAnimatingRef.current = true;

      const step = (now) => {
        const progress = clamp((now - startedAt) / durationMs, 0, 1);
        const eased = timeframe === 'Tick' ? progress * (2 - progress) : easeInOutCubic(progress);
        timeScale.setVisibleLogicalRange({
          from: fromStart + (fromTarget - fromStart) * eased,
          to: toStart + (toTarget - toStart) * eased
        });
        forceRightPriceAutoScale();
        syncLiveMarkerToRendered();
        onViewportChangeRef.current?.();
        if (progress >= 1) {
          liveShiftRafRef.current = null;
          isLiveShiftAnimatingRef.current = false;
          return;
        }
        liveShiftRafRef.current = requestAnimationFrame(step);
      };
      liveShiftRafRef.current = requestAnimationFrame(step);
    };
    const zoomOut = (factor = 1.25) => {
      const totalBars = totalBarsCountRef.current > 0
        ? totalBarsCountRef.current
        : (timeframe === 'Tick' ? lineDataRef.current.length : candleDataRef.current.length);
      if (totalBars < 2) return;

      stopAutoZoomAnimation();
      stopLiveShiftAnimation();

      const currentRange = timeScale.getVisibleLogicalRange();
      const defaultBars = resolveCenteredVisibleWindow(totalBars);
      const currentWindow = (currentRange && Number.isFinite(currentRange.to - currentRange.from))
        ? (currentRange.to - currentRange.from)
        : (userCustomVisibleBarsRef.current ?? defaultBars);

      if (timeframe === 'Tick') {
        const targetBars = clamp(Math.round(Math.max(currentWindow, defaultBars) * factor), defaultBars, CENTERED_LIVE_MAX_BARS);
        userCustomVisibleBarsRef.current = targetBars;
        if (keepLatestCentered) {
          applyCenteredRange();
        } else {
          const latestBarIndex = totalBars - 1;
          const to = latestBarIndex + Math.max(0, forwardSpaceBars);
          const from = to - targetBars;
          timeScale.setVisibleLogicalRange({ from, to });
        }
      } else {
        const targetBars = clamp(Math.round(currentWindow * factor), LIVE_ZOOM_MIN_BARS, LIVE_ZOOM_MAX_BARS);
        const center = currentRange ? (currentRange.from + currentRange.to) / 2 : (totalBars - 1);
        timeScale.setVisibleLogicalRange({
          from: center - targetBars / 2,
          to: center + targetBars / 2
        });
      }
      forceRightPriceAutoScale();
      syncLiveMarkerToRendered();
      onViewportChangeRef.current?.();
    };

    const zoomIn = (factor = 1.25) => {
      const totalBars = totalBarsCountRef.current > 0
        ? totalBarsCountRef.current
        : (timeframe === 'Tick' ? lineDataRef.current.length : candleDataRef.current.length);
      if (totalBars < 2) return;

      stopAutoZoomAnimation();
      stopLiveShiftAnimation();

      const currentRange = timeScale.getVisibleLogicalRange();
      const defaultBars = resolveCenteredVisibleWindow(totalBars);
      const currentWindow = (currentRange && Number.isFinite(currentRange.to - currentRange.from))
        ? (currentRange.to - currentRange.from)
        : (userCustomVisibleBarsRef.current ?? defaultBars);

      if (timeframe === 'Tick') {
        // In live mode: cannot zoom in beyond defaultBars (defaultBars is the maximum zoom level)
        const targetBars = clamp(Math.round(currentWindow / factor), defaultBars, CENTERED_LIVE_MAX_BARS);
        userCustomVisibleBarsRef.current = targetBars;
        if (keepLatestCentered) {
          applyCenteredRange();
        } else {
          const latestBarIndex = totalBars - 1;
          const to = latestBarIndex + Math.max(0, forwardSpaceBars);
          const from = to - targetBars;
          timeScale.setVisibleLogicalRange({ from, to });
        }
      } else {
        const targetBars = clamp(Math.round(currentWindow / factor), LIVE_ZOOM_MIN_BARS, LIVE_ZOOM_MAX_BARS);
        const center = currentRange ? (currentRange.from + currentRange.to) / 2 : (totalBars - 1);
        timeScale.setVisibleLogicalRange({
          from: center - targetBars / 2,
          to: center + targetBars / 2
        });
      }
      forceRightPriceAutoScale();
      syncLiveMarkerToRendered();
      onViewportChangeRef.current?.();
    };

    const resetZoom = () => {
      userCustomVisibleBarsRef.current = null;
      stopAutoZoomAnimation();
      stopLiveShiftAnimation();
      if (keepLatestCentered) {
        applyCenteredRange();
      } else {
        timeScale.fitContent();
      }
      forceRightPriceAutoScale();
      syncLiveMarkerToRendered();
      onViewportChangeRef.current?.();
    };

    zoomInRef.current = zoomIn;
    zoomOutRef.current = zoomOut;
    resetZoomRef.current = resetZoom;
    scheduleLiveReturnRef.current = scheduleLiveReturn;
    animateZoomToRecentRef.current = animateZoomToRecentBars;
    smoothShiftToNextBarRef.current = smoothShiftToNextBar;
    applyFixedZoomRef.current = applyFixedZoomRange;
    applyCenteredRangeRef.current = applyCenteredRange;
    getTargetLiveRangeRef.current = getTargetLiveRange;
    chart.timeScale().fitContent();
    const centeredRangeApplied = applyCenteredRange();
    if (!centeredRangeApplied && !applyFixedZoomRange() && !disableZoomToLatest) {
      clearInitialZoomTimer();
      initialZoomTimerRef.current = setTimeout(() => {
        animateZoomToRecentBars();
      }, INITIAL_ZOOM_TRIGGER_DELAY_MS);
    }
    syncLiveMarker(series, chart, latestPrice, latestTime, onLiveMarkerChangeRef.current);
    let resizeFrameId = null;
    let lastAppliedWidth = Math.max(1, Math.round(container.clientWidth));
    let lastAppliedHeight = Math.max(1, Math.round(container.clientHeight));
    const applyChartResize = (width, height) => {
      if (width === lastAppliedWidth && height === lastAppliedHeight) {
        return;
      }
      lastAppliedWidth = width;
      lastAppliedHeight = height;
      chart.applyOptions({ width, height });
      forceRightPriceAutoScale();
      if (keepLatestCentered) {
        applyCenteredRange();
      }
      syncLiveMarkerToRendered();
      onViewportChangeRef.current?.();
    };
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const width = Math.max(1, Math.round(entry.contentRect.width));
      const height = Math.max(1, Math.round(entry.contentRect.height));
      if (width === lastAppliedWidth && height === lastAppliedHeight) {
        return;
      }
      if (resizeFrameId !== null) {
        cancelAnimationFrame(resizeFrameId);
      }
      resizeFrameId = requestAnimationFrame(() => {
        resizeFrameId = null;
        applyChartResize(width, height);
      });
    });
    resizeObserver.observe(container);
    const syncMarkerOnScale = (range) => {
      syncLiveMarkerToRendered();
      onViewportChangeRef.current?.();
      if (!range || isAutoZoomingRef.current || isLiveShiftAnimatingRef.current) {
        return;
      }

      if (timeframe === 'Tick') {
        const totalBars = totalBarsCountRef.current > 0
          ? totalBarsCountRef.current
          : lineDataRef.current.length;
        const defaultBars = resolveCenteredVisibleWindow(totalBars);
        const windowSize = range.to - range.from;

        if (windowSize < defaultBars) {
          // Clamped: cannot zoom in beyond default level!
          userCustomVisibleBarsRef.current = defaultBars;
          const currentCenter = (range.from + range.to) / 2;
          const halfWindow = defaultBars / 2;
          timeScale.setVisibleLogicalRange({
            from: currentCenter - halfWindow,
            to: currentCenter + halfWindow
          });
          return;
        } else if (windowSize > CENTERED_LIVE_MAX_BARS) {
          userCustomVisibleBarsRef.current = CENTERED_LIVE_MAX_BARS;
          const currentCenter = (range.from + range.to) / 2;
          const halfWindow = CENTERED_LIVE_MAX_BARS / 2;
          timeScale.setVisibleLogicalRange({
            from: currentCenter - halfWindow,
            to: currentCenter + halfWindow
          });
          return;
        } else {
          userCustomVisibleBarsRef.current = windowSize;
        }
      }

      if (keepLatestCentered) {
        const totalBars = totalBarsCountRef.current > 0
          ? totalBarsCountRef.current
          : (timeframe === 'Tick' ? lineDataRef.current.length : candleDataRef.current.length);
        const latestBarIndex = totalBars - 1;
        const currentCenter = (range.from + range.to) / 2;
        const awayFromLive = Math.abs(currentCenter - latestBarIndex) > 3;
        isAwayFromLiveRef.current = awayFromLive;
        if (awayFromLive) {
          scheduleLiveReturn();
        }
        return;
      }
      const barsInfo = series.barsInLogicalRange(range);
      const awayFromLive = (barsInfo?.barsAfter ?? 0) > 3;
      isAwayFromLiveRef.current = awayFromLive;
      if (awayFromLive) {
        scheduleLiveReturn();
      }
    };
    timeScale.subscribeVisibleLogicalRangeChange(syncMarkerOnScale);
    chartRef.current = chart;
    seriesRef.current = series;

    const handleWheel = (e) => {
      // Zoom with mouse wheel / Ctrl+wheel / trackpad pinch
      e.preventDefault();
      e.stopPropagation();

      const factor = e.ctrlKey ? 1.08 : 1.15;
      if (e.deltaY < 0) {
        // Scroll up -> zoom in (capped at default in live mode)
        zoomIn(factor);
      } else if (e.deltaY > 0) {
        // Scroll down -> zoom out
        zoomOut(factor);
      }
    };

    let touchStartDist = 0;
    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length === 2) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        touchStartDist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length === 2 && touchStartDist > 0) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const currentDist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        const diff = currentDist - touchStartDist;
        if (Math.abs(diff) > 8) {
          e.preventDefault();
          if (diff > 0) {
            zoomIn(1.06);
          } else {
            zoomOut(1.06);
          }
          touchStartDist = currentDist;
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (!e.touches || e.touches.length < 2) {
        touchStartDist = 0;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      resizeObserver.disconnect();
      if (resizeFrameId !== null) {
        cancelAnimationFrame(resizeFrameId);
        resizeFrameId = null;
      }
      clearAutoReturnTimer();
      clearInitialZoomTimer();
      stopAutoZoomAnimation();
      stopLiveShiftAnimation();
      isLiveShiftAnimatingRef.current = false;
      getTargetLiveRangeRef.current = () => null;
      stopCandleVisualAnimation();
      stopLineVisualAnimation();
      candleVisualTargetRef.current = null;
      lineVisualTargetRef.current = null;
      lineVisualPointRef.current = null;
      scheduleLiveReturnRef.current = () => { };
      animateZoomToRecentRef.current = () => { };
      smoothShiftToNextBarRef.current = () => { };
      applyFixedZoomRef.current = () => false;
      applyCenteredRangeRef.current = () => false;
      zoomInRef.current = () => { };
      zoomOutRef.current = () => { };
      resetZoomRef.current = () => { };
      timeScale.unsubscribeVisibleLogicalRangeChange(syncMarkerOnScale);
      clearReferencePriceLines();
      priceLineRef.current = null;
      seriesRef.current = null;
      chartRef.current = null;
      onLiveMarkerChangeRef.current?.(null);
      chart.remove();
    };
  }, [containerRef, timeframe, chartType, disableInteractions, hideTimeScale, disableZoomToLatest, fixedZoomPercent, keepLatestCentered, centeredVisibleBars, forwardSpaceBars, smoothShiftDurationMs, chartBackgroundColor, syncPinnedPriceRange, pinPriceScale, showLivePriceLine, clearReferencePriceLines, syncReferencePriceLines, stopCandleVisualAnimation, stopLineVisualAnimation]);
  useEffect(() => {
    syncReferencePriceLines();
    return () => {
      clearReferencePriceLines();
    };
  }, [clearReferencePriceLines, referencePriceLines, syncReferencePriceLines]);
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) {
      return;
    }
    const shouldUseAreaSeries = chartTypeRef.current === 'area' || timeframe === 'Tick';
    if (shouldUseAreaSeries) {
      const areaData = resolveAreaSeriesData(timeframe, lineData, candleData);
      stopCandleVisualAnimation();
      candleVisualTargetRef.current = null;
      stopLineVisualAnimation();
      series.setData(areaData);
      totalBarsCountRef.current = areaData.length;
      if (timeframe === 'Tick') {
        candleStateRef.current = null;
        realCandleStateRef.current = null;
      }
      else {
        const latestCandle = candleData[candleData.length - 1] ?? null;
        candleStateRef.current = latestCandle;
        realCandleStateRef.current = latestCandle;
      }
      const latestPoint = areaData[areaData.length - 1];
      const latestTime = latestPoint?.time ?? Math.floor(Date.now() / 1000);
      const latestPrice = latestPoint?.value ?? currentPriceRef.current ?? startingPriceRef.current;
      lineVisualPointRef.current = {
        time: latestTime,
        value: latestPrice
      };
      lineVisualTargetRef.current = lineVisualPointRef.current;
      lastTimeRef.current = latestTime;
      markerTimeRef.current = latestTime;
      currentPriceRef.current = latestPrice;
      scaleCenterRef.current = latestPrice;
      syncPinnedPriceRange();
      priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(latestPrice, resolveInitialPriceDirection({
        timeframe,
        chartType: chartTypeRef.current,
        lineData,
        candleData,
        latestPrice
      })));
      syncLiveMarker(series, chart, latestPrice, latestTime, onLiveMarkerChangeRef.current);
      onPriceChangeRef.current?.(latestPrice);
      onViewportChangeRef.current?.();
      const centeredRangeApplied = applyCenteredRangeRef.current();
      const fixedZoomApplied = centeredRangeApplied ? false : applyFixedZoomRef.current();
      if (!centeredRangeApplied && !fixedZoomApplied && !disableZoomToLatest) {
        animateZoomToRecentRef.current();
      }
      return;
    }
    stopCandleVisualAnimation();
    stopLineVisualAnimation();
    candleVisualTargetRef.current = null;
    lineVisualTargetRef.current = null;
    lineVisualPointRef.current = null;
    series.setData(candleData);
    totalBarsCountRef.current = candleData.length;
    const latestCandle = candleData[candleData.length - 1] ?? null;
    candleStateRef.current = latestCandle;
    realCandleStateRef.current = latestCandle;
    const latestTime = latestCandle?.time ?? Math.floor(Date.now() / 1000);
    const latestPrice = latestCandle?.close ?? currentPriceRef.current ?? startingPriceRef.current;
    lastTimeRef.current = latestTime;
    markerTimeRef.current = latestTime;
    currentPriceRef.current = latestPrice;
    scaleCenterRef.current = latestPrice;
    syncPinnedPriceRange();
    priceLineRef.current?.applyOptions(resolveLivePriceLineOptions(latestPrice, resolveInitialPriceDirection({
      timeframe,
      chartType: chartTypeRef.current,
      lineData,
      candleData,
      latestPrice
    })));
    syncLiveMarker(series, chart, latestPrice, latestTime, onLiveMarkerChangeRef.current);
    onPriceChangeRef.current?.(latestPrice);
    onViewportChangeRef.current?.();
    const centeredRangeApplied = applyCenteredRangeRef.current();
    const fixedZoomApplied = centeredRangeApplied ? false : applyFixedZoomRef.current();
    if (!centeredRangeApplied && !fixedZoomApplied && !disableZoomToLatest) {
      animateZoomToRecentRef.current();
    }
  }, [candleData, lineData, timeframe, chartType, disableZoomToLatest, syncPinnedPriceRange, stopCandleVisualAnimation, stopLineVisualAnimation]);
  const pushPrice = useCallback((price, time, sourceTick) => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series) {
      return;
    }
    const previousTime = lastTimeRef.current;
    const previousPrice = currentPriceRef.current;
    const livePriceDirection = resolveLivePriceDirection(price, previousPrice, sourceTick);
    livePriceDirectionRef.current = livePriceDirection;
    const nextTime = timeframe === 'Tick'
      ? resolveNextTimestamp(lastTimeRef.current, time)
      : resolveCandleTimestamp(lastTimeRef.current, time);
    lastTimeRef.current = nextTime;
    currentPriceRef.current = price;
    let markerTime = nextTime;
    let shouldScroll = false;
    const shouldUseAreaSeries = chartTypeRef.current === 'area' || timeframe === 'Tick';
    if (timeframe === 'Tick') {
      animateVisualLinePoint({
        time: nextTime,
        value: price
      });
      shouldScroll = nextTime > previousTime;
    }
    else {
      const intervalSeconds = timeframeToSeconds(timeframe);
      const sourceTime = Number.isFinite(sourceTick?.time) ? Math.trunc(sourceTick.time) : nextTime;
      const candleOpenTime = floorToBucket(sourceTime, intervalSeconds);
      const sourceOpen = Number.isFinite(sourceTick?.open) ? sourceTick.open : price;
      const sourceClose = Number.isFinite(sourceTick?.close) ? sourceTick.close : price;
      const sourceHigh = Number.isFinite(sourceTick?.high) ? sourceTick.high : Math.max(sourceOpen, sourceClose);
      const sourceLow = Number.isFinite(sourceTick?.low) ? sourceTick.low : Math.min(sourceOpen, sourceClose);
      const previousRealCandle = realCandleStateRef.current;
      const nextRealCandle = !previousRealCandle || candleOpenTime > previousRealCandle.time
        ? {
          time: candleOpenTime,
          open: sourceOpen,
          high: Math.max(sourceHigh, sourceOpen, sourceClose),
          low: Math.min(sourceLow, sourceOpen, sourceClose),
          close: sourceClose
        }
        : {
          time: previousRealCandle.time,
          open: previousRealCandle.open,
          high: Math.max(previousRealCandle.high, sourceHigh, sourceClose),
          low: Math.min(previousRealCandle.low, sourceLow, sourceClose),
          close: sourceClose
        };
      realCandleStateRef.current = nextRealCandle;
      shouldScroll = !previousRealCandle || nextRealCandle.time > previousRealCandle.time;
      if (shouldUseAreaSeries) {
        candleStateRef.current = nextRealCandle;
        markerTime = nextRealCandle.time;
        animateVisualLinePoint({
          time: nextRealCandle.time,
          value: nextRealCandle.close
        });
      }
      else {
        const visualCandle = buildVisualCandle(nextRealCandle, price, timeframe, sourceTick, series);
        markerTime = visualCandle.time;
        shouldScroll = !previousRealCandle || visualCandle.time > previousRealCandle.time;
        animateVisualCandle(visualCandle);
      }
    }
    markerTimeRef.current = markerTime;
    syncPinnedPriceRange();
    if (shouldScroll) {
      totalBarsCountRef.current += 1;
      if (keepLatestCentered) {
        smoothShiftToNextBarRef.current();
      }
      else if (isAwayFromLiveRef.current) {
        scheduleLiveReturnRef.current(0);
      }
      else {
        smoothShiftToNextBarRef.current();
      }
    } else if (keepLatestCentered && !isAwayFromLiveRef.current) {
      const currentRange = chartRef.current?.timeScale().getVisibleLogicalRange();
      const target = getTargetLiveRangeRef.current?.();
      if (currentRange && target && Math.abs(target.to - currentRange.to) > 2.5) {
        smoothShiftToNextBarRef.current();
      }
    }
    onViewportChangeRef.current?.();
  }, [timeframe, keepLatestCentered, syncPinnedPriceRange, animateVisualCandle, animateVisualLinePoint]);

  const getTimeCoordinate = useCallback((time) => {
    const chart = chartRef.current;
    if (!chart || !Number.isFinite(time)) {
      return null;
    }
    const timeScale = chart.timeScale();
    const directX = timeScale.timeToCoordinate(time);
    if (directX !== null) {
      return directX;
    }

    const currentData = timeframe === 'Tick' ? lineDataRef.current : candleDataRef.current;
    const firstTime = currentData && currentData.length > 0 ? currentData[0]?.time : null;
    const lastTime = lastTimeRef.current ?? (currentData && currentData.length > 0 ? currentData[currentData.length - 1]?.time : null);

    const intervalSeconds = timeframe === 'Tick' ? (1 / 3) : timeframeToSeconds(timeframe);
    const secondsPerBar = Math.max(0.05, intervalSeconds);

    let actualBarSpacing = 10;
    try {
      const visibleRange = timeScale.getVisibleLogicalRange();
      if (
        visibleRange &&
        Number.isFinite(visibleRange.from) &&
        Number.isFinite(visibleRange.to) &&
        visibleRange.to > visibleRange.from
      ) {
        const c0 = timeScale.logicalToCoordinate(visibleRange.from);
        const c1 = timeScale.logicalToCoordinate(visibleRange.from + 1);
        if (c0 !== null && c1 !== null && Number.isFinite(c1 - c0) && Math.abs(c1 - c0) > 0.01) {
          actualBarSpacing = Math.abs(c1 - c0);
        } else {
          const width = containerRef?.current?.clientWidth || 800;
          actualBarSpacing = width / (visibleRange.to - visibleRange.from);
        }
      } else {
        actualBarSpacing = timeScale.options().barSpacing ?? BAR_SPACING_BY_TIMEFRAME[timeframe] ?? 10;
      }
    } catch (_err) {
      actualBarSpacing = 10;
    }
    actualBarSpacing = Math.max(0.5, actualBarSpacing);

    // 1. Future time (e.g. trade expiry countdown)
    if (Number.isFinite(lastTime) && time > lastTime) {
      let lastX = timeScale.timeToCoordinate(lastTime);
      if (lastX === null) {
        const lastIdx = timeScale.timeToIndex(lastTime, true);
        if (lastIdx !== null) {
          lastX = timeScale.logicalToCoordinate(lastIdx);
        }
      }
      if (lastX !== null) {
        const barsInFuture = (time - lastTime) / secondsPerBar;
        return lastX + (barsInFuture * actualBarSpacing);
      }
    }

    // 2. Past time before the first loaded bar
    if (Number.isFinite(firstTime) && time < firstTime) {
      let firstX = timeScale.timeToCoordinate(firstTime);
      if (firstX === null) {
        const firstIdx = timeScale.timeToIndex(firstTime, true);
        if (firstIdx !== null) {
          firstX = timeScale.logicalToCoordinate(firstIdx);
        }
      }
      if (firstX !== null) {
        const barsBefore = (time - firstTime) / secondsPerBar;
        return firstX + (barsBefore * actualBarSpacing);
      }
    }

    // 3. Inside the range of loaded data: find nearest bar
    const nearestIdx = timeScale.timeToIndex(time, true);
    if (nearestIdx !== null) {
      const nearestX = timeScale.logicalToCoordinate(nearestIdx);
      if (nearestX !== null) {
        return nearestX;
      }
    }

    // 4. Fallback past extrapolation
    if (Number.isFinite(lastTime)) {
      let lastX = timeScale.timeToCoordinate(lastTime);
      if (lastX === null) {
        const lastIdx = timeScale.timeToIndex(lastTime, true);
        if (lastIdx !== null) {
          lastX = timeScale.logicalToCoordinate(lastIdx);
        }
      }
      if (lastX !== null) {
        const barsDiff = (time - lastTime) / secondsPerBar;
        return lastX + (barsDiff * actualBarSpacing);
      }
    }

    return null;
  }, [containerRef, timeframe]);

  const getCoordinates = useCallback((price, time) => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || !Number.isFinite(price) || !Number.isFinite(time)) {
      return null;
    }
    const y = series.priceToCoordinate(price);
    const x = getTimeCoordinate(time);
    if (x === null || y === null) {
      return null;
    }
    return { x, y };
  }, [getTimeCoordinate]);
  const getPriceTimeFromCoordinates = useCallback((x, y) => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) {
      return null;
    }
    const rawTime = chart.timeScale().coordinateToTime(x);
    const price = series.coordinateToPrice(y);
    if (rawTime === null || price === null) {
      return null;
    }
    const time = typeof rawTime === 'number'
      ? Math.round(rawTime)
      : Math.floor(Date.now() / 1000);
    return {
      time,
      price
    };
  }, []);
  const zoomIn = useCallback((factor) => {
    zoomInRef.current?.(factor);
  }, []);

  const zoomOut = useCallback((factor) => {
    zoomOutRef.current?.(factor);
  }, []);

  const resetZoom = useCallback(() => {
    resetZoomRef.current?.();
  }, []);

  const getPlottingBounds = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;
    const containerWidth = container.clientWidth;
    if (!containerWidth || containerWidth <= 0) return null;

    const chart = chartRef.current;
    let timeScaleWidth = null;
    let priceScaleWidth = null;

    if (chart) {
      try {
        timeScaleWidth = chart.timeScale?.()?.width?.();
      } catch (_e) {}
      try {
        priceScaleWidth = chart.priceScale?.('right')?.width?.();
      } catch (_e) {}
    }

    const resolvedPriceScaleWidth = Number.isFinite(priceScaleWidth) && priceScaleWidth > 0
      ? priceScaleWidth
      : 60;

    const usableWidth = Number.isFinite(timeScaleWidth) && timeScaleWidth > 0
      ? timeScaleWidth
      : Math.max(100, containerWidth - resolvedPriceScaleWidth);

        // The chart contains 6 vertical grid columns across its width.
    // Column 1 is the left edge (not occupied by markers).
    // Columns 2–5 are the designated area for the trade markers.
    // Column 6 is reserved for the Y-axis price labels.
    const columnWidth = containerWidth / 6;
    const rocketOffset = 30;
    const startBound = columnWidth + rocketOffset;
    const endBound = Math.min(columnWidth * 5, usableWidth - 6);

    return {
      containerWidth,
      usableWidth,
      priceScaleWidth: resolvedPriceScaleWidth,
      columnWidth,
      startBound,
      endBound,
    };
  }, [containerRef]);

  return {
    pushPrice,
    getCoordinates,
    getTimeCoordinate,
    getPriceTimeFromCoordinates,
    getPlottingBounds,
    zoomIn,
    zoomOut,
    resetZoom,
  };
}
function resolveLivePriceLineOptions(price, direction = 'up') {
  const color = direction === 'down' ? LIVE_PRICE_DOWN_COLOR : LIVE_PRICE_UP_COLOR;
  return {
    price,
    color,
    lineWidth: 1,
    lineStyle: LineStyle.Dashed,
    axisLabelVisible: true,
    axisLabelColor: color,
    axisLabelTextColor: LIVE_PRICE_LABEL_TEXT_COLOR,
    title: ''
  };
}
function resolveLivePriceDirection(price, previousPrice, sourceTick) {
  const explicitDirection = String(sourceTick?.direction || '').toLowerCase();
  if (explicitDirection === 'down') {
    return 'down';
  }
  if (explicitDirection === 'up') {
    return 'up';
  }
  if (Number.isFinite(price) && Number.isFinite(previousPrice)) {
    return price < previousPrice ? 'down' : 'up';
  }
  return 'up';
}
function resolveInitialPriceDirection({ timeframe, chartType, lineData, candleData, latestPrice }) {
  if (chartType === 'area' || timeframe === 'Tick') {
    const previousPoint = Array.isArray(lineData) && lineData.length > 1
      ? lineData[lineData.length - 2]
      : null;
    return resolveLivePriceDirection(latestPrice, previousPoint?.value, null);
  }
  const latestCandle = Array.isArray(candleData) && candleData.length > 0
    ? candleData[candleData.length - 1]
    : null;
  if (latestCandle && Number.isFinite(latestCandle.close) && Number.isFinite(latestCandle.open)) {
    return latestCandle.close < latestCandle.open ? 'down' : 'up';
  }
  return 'up';
}
function resolveNextTimestamp(lastTime, incomingTime) {
  const fallbackTime = Math.round((Date.now() / 1000) * 10) / 10;
  const candidate = Number.isFinite(incomingTime)
    ? Math.round(incomingTime * 10) / 10
    : fallbackTime;
  if (!Number.isFinite(lastTime)) {
    return candidate;
  }
  return candidate > lastTime ? candidate : lastTime;
}
function resolveCandleTimestamp(lastTime, incomingTime) {
  const fallbackTime = Math.floor(Date.now() / 1000);
  const candidate = Number.isFinite(incomingTime) ? Math.trunc(incomingTime) : fallbackTime;
  return (candidate < lastTime ? lastTime : candidate);
}
function resolveCandleVisualEasingMs(timeframe) {
  return CANDLE_VISUAL_EASING_MS_BY_TIMEFRAME[timeframe] ?? 440;
}
function resolveLineVisualEasingMs(timeframe) {
  if (timeframe === "Tick") {
    return 16;
  }

  return LINE_VISUAL_EASING_MS_BY_TIMEFRAME[timeframe] ?? 380;
}
function resolveLineDrawDurationMs(timeframe, previousPoint, targetPoint) {
  const priceDuration =
    timeframe === "Tick" ? 16 : resolveLineVisualEasingMs(timeframe);
  const timeDeltaSeconds = Number.isFinite(previousPoint?.time) && Number.isFinite(targetPoint?.time)
    ? Math.max(0, targetPoint.time - previousPoint.time)
    : 0;
  if (timeDeltaSeconds <= 0.001) {
    return priceDuration;
  }
  const liveShiftDuration = LIVE_SHIFT_ANIMATION_MS_BY_TIMEFRAME[timeframe] ?? 3200;
  const intervalSeconds = timeframe === 'Tick' ? 0.45 : Math.max(1, timeframeToSeconds(timeframe));
  const slotRatio = clamp(timeDeltaSeconds / intervalSeconds, 0.15, 1);
  return clamp(liveShiftDuration * slotRatio, priceDuration, liveShiftDuration);
}
function buildNewLinePointStart(previousPoint, targetPoint) {
  const anchorValue = Number.isFinite(previousPoint?.value)
    ? previousPoint.value
    : targetPoint.value;
  return normalizeLinePoint({
    time: targetPoint.time,
    value: anchorValue
  });
}
function interpolateLinePoint(previousPoint, targetPoint, alpha) {
  const safeTarget = normalizeLinePoint(targetPoint);
  const easedAlpha = clamp(alpha, 0, 1);
  const fromValue = Number.isFinite(targetPoint?.fromValue)
    ? targetPoint.fromValue
    : (Number.isFinite(previousPoint?.value) ? previousPoint.value : safeTarget.value);
  return normalizeLinePoint({
    time: safeTarget.time,
    value: lerpPrice(fromValue, safeTarget.value, easedAlpha)
  });
}
function normalizeLinePoint(point) {
  const value = Number.isFinite(point?.value) ? point.value : MIN_PRICE_VALUE;
  const fallbackTime = Math.round((Date.now() / 1000) * 10) / 10;
  const time = Number.isFinite(point?.time) ? Math.round(point.time * 10) / 10 : fallbackTime;
  return {
    time,
    value: Math.max(MIN_PRICE_VALUE, value)
  };
}
function areLinePointsVisuallyClose(currentPoint, targetPoint) {
  if (!currentPoint || !targetPoint) {
    return false;
  }
  const safeTarget = normalizeLinePoint(targetPoint);
  // const tolerance = Math.max(Math.abs(safeTarget.value), 1) * 0.00000008;
  const tolerance = 0;
  return Math.abs(currentPoint.value - safeTarget.value) <= tolerance;
}
function buildNewVisualCandleStart(previousVisual, target) {
  const anchor = Number.isFinite(target.open)
    ? target.open
    : (Number.isFinite(previousVisual?.close) ? previousVisual.close : target.close);
  return normalizeVisualCandle({
    time: target.time,
    open: anchor,
    high: anchor,
    low: anchor,
    close: anchor
  });
}
function interpolateVisualCandle(previous, target, alpha) {
  const safeTarget = normalizeVisualCandle(target);
  if (!previous || previous.time !== safeTarget.time) {
    return interpolateVisualCandle(buildNewVisualCandleStart(previous, safeTarget), safeTarget, alpha);
  }
  const easedAlpha = clamp(alpha, 0, 1);
  return normalizeVisualCandle({
    time: safeTarget.time,
    open: lerpPrice(previous.open, safeTarget.open, easedAlpha),
    high: lerpPrice(previous.high, safeTarget.high, easedAlpha),
    low: lerpPrice(previous.low, safeTarget.low, easedAlpha),
    close: lerpPrice(previous.close, safeTarget.close, easedAlpha)
  });
}
function normalizeVisualCandle(candle) {
  const open = Number.isFinite(candle?.open) ? candle.open : 0;
  const close = Number.isFinite(candle?.close) ? candle.close : open;
  const rawHigh = Number.isFinite(candle?.high) ? candle.high : Math.max(open, close);
  const rawLow = Number.isFinite(candle?.low) ? candle.low : Math.min(open, close);
  return {
    time: candle?.time,
    open,
    high: Math.max(rawHigh, open, close),
    low: Math.min(rawLow, open, close),
    close
  };
}
function areCandlesVisuallyClose(current, target) {
  if (!current || !target || current.time !== target.time) {
    return false;
  }
  const safeTarget = normalizeVisualCandle(target);
  const tolerance = Math.max(Math.abs(safeTarget.close), Math.abs(safeTarget.open), 1) * 0.00000008;
  return Math.abs(current.open - safeTarget.open) <= tolerance &&
    Math.abs(current.high - safeTarget.high) <= tolerance &&
    Math.abs(current.low - safeTarget.low) <= tolerance &&
    Math.abs(current.close - safeTarget.close) <= tolerance;
}
function lerpPrice(from, to, alpha) {
  const safeFrom = Number.isFinite(from) ? from : to;
  const safeTo = Number.isFinite(to) ? to : safeFrom;
  return safeFrom + ((safeTo - safeFrom) * alpha);
}
function buildVisualCandle(realCandle, livePrice, timeframe, sourceTick, series) {
  const visual = {
    time: realCandle.time,
    open: realCandle.open,
    high: Math.max(realCandle.high, livePrice),
    low: Math.min(realCandle.low, livePrice),
    close: livePrice
  };
  if (timeframe !== '5s') {
    return visual;
  }
  const closeY = series.priceToCoordinate(visual.close);
  const openY = series.priceToCoordinate(visual.open);
  const highY = series.priceToCoordinate(visual.high);
  const lowY = series.priceToCoordinate(visual.low);
  if (![closeY, openY, highY, lowY].every((value) => Number.isFinite(value))) {
    return visual;
  }
  const seedBase = Math.trunc(realCandle.time) + (Number.isFinite(sourceTick?.sourceTimeMs)
    ? Math.trunc(sourceTick.sourceTimeMs / 1000)
    : 0);
  const minBodyPx = SMALL_CANDLE_BODY_MIN_PX +
    (seededNoise(seedBase + 13) * (SMALL_CANDLE_BODY_MAX_PX - SMALL_CANDLE_BODY_MIN_PX));
  const minUpperWickPx = SMALL_CANDLE_WICK_MIN_PX +
    (seededNoise(seedBase + 23) * (SMALL_CANDLE_WICK_MAX_PX - SMALL_CANDLE_WICK_MIN_PX));
  const minLowerWickPx = SMALL_CANDLE_WICK_MIN_PX +
    (seededNoise(seedBase + 31) * (SMALL_CANDLE_WICK_MAX_PX - SMALL_CANDLE_WICK_MIN_PX));
  let open = visual.open;
  const close = visual.close;
  let bodyDirection = Math.sign(close - open);
  if (bodyDirection === 0) {
    bodyDirection = sourceTick?.direction === 'down'
      ? -1
      : sourceTick?.direction === 'up'
        ? 1
        : (seededNoise(seedBase + 41) > 0.5 ? 1 : -1);
  }
  if (Math.abs(openY - closeY) < minBodyPx) {
    const targetOpenY = bodyDirection > 0 ? closeY + minBodyPx : closeY - minBodyPx;
    const projectedOpen = series.coordinateToPrice(targetOpenY);
    if (Number.isFinite(projectedOpen)) {
      open = projectedOpen;
    }
  }
  let high = Math.max(visual.high, open, close);
  let low = Math.min(visual.low, open, close);
  const candleTop = Math.max(open, close);
  const candleBottom = Math.min(open, close);
  const topY = series.priceToCoordinate(candleTop);
  const bottomY = series.priceToCoordinate(candleBottom);
  if (Number.isFinite(topY)) {
    const currentHighY = series.priceToCoordinate(high);
    const upperWickPx = Number.isFinite(currentHighY) ? Math.max(0, topY - currentHighY) : 0;
    if (upperWickPx < minUpperWickPx) {
      const projectedHigh = series.coordinateToPrice(topY - minUpperWickPx);
      if (Number.isFinite(projectedHigh)) {
        high = Math.max(projectedHigh, candleTop);
      }
    }
  }
  if (Number.isFinite(bottomY)) {
    const currentLowY = series.priceToCoordinate(low);
    const lowerWickPx = Number.isFinite(currentLowY) ? Math.max(0, currentLowY - bottomY) : 0;
    if (lowerWickPx < minLowerWickPx) {
      const projectedLow = series.coordinateToPrice(bottomY + minLowerWickPx);
      if (Number.isFinite(projectedLow)) {
        low = Math.min(projectedLow, candleBottom);
      }
    }
  }
  return {
    time: visual.time,
    open,
    high: Math.max(high, open, close),
    low: Math.min(low, open, close),
    close
  };
}
function seededNoise(seed) {
  const raw = Math.sin(seed * 12.9898) * 43758.5453123;
  return raw - Math.floor(raw);
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
function easeInOutCubic(progress) {
  const clamped = clamp(progress, 0, 1);
  return clamped < 0.5
    ? 4 * clamped * clamped * clamped
    : 1 - Math.pow(-2 * clamped + 2, 3) / 2;
}
function resolveAreaSeriesData(timeframe, lineData, candleData) {
  if (timeframe === 'Tick') {
    return lineData;
  }
  return candleData.map((candle) => ({
    time: candle.time,
    value: candle.close
  }));
}
function getPriceFormatOptions(value) {
  if (value >= 1000) {
    return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }
  if (value >= 1) {
    return { minimumFractionDigits: 3, maximumFractionDigits: 3 };
  }
  if (value >= 0.1) {
    return { minimumFractionDigits: 4, maximumFractionDigits: 4 };
  }
  return { minimumFractionDigits: 5, maximumFractionDigits: 5 };
}
function resolveAdaptivePriceScaleStep(price, options = {}) {
  const absolute = Math.abs(price);
  if (!Number.isFinite(absolute) || absolute <= 0) {
    return null;
  }
  const timeframe = options?.timeframe ?? 'Tick';
  const levels = Math.max(2, Number.isFinite(options?.levels) ? Math.round(options.levels) : DEFAULT_PRICE_SCALE_LEVELS);
  const lineData = Array.isArray(options?.lineData) ? options.lineData : [];
  const candleData = Array.isArray(options?.candleData) ? options.candleData : [];
  const recentRange = resolveRecentPriceRange(timeframe, lineData, candleData);
  const minSpanRatio = timeframe === 'Tick' ? DYNAMIC_TICK_MIN_SPAN_RATIO : DYNAMIC_CANDLE_MIN_SPAN_RATIO;
  const minSpan = absolute * minSpanRatio;
  const maxSpan = absolute * DYNAMIC_MAX_SPAN_RATIO;
  const desiredSpan = clamp(Math.max(recentRange * DYNAMIC_RANGE_HEADROOM, minSpan), minSpan, maxSpan);
  const rawStep = desiredSpan / levels;
  const fallbackStep = resolveLegacyAdaptivePriceScaleStep(absolute);
  if (!Number.isFinite(rawStep) || rawStep <= 0) {
    return fallbackStep;
  }
  const niceStep = toNiceStep(rawStep);
  return Number.isFinite(niceStep) && niceStep > 0 ? niceStep : fallbackStep;
}
function resolveRecentPriceRange(timeframe, lineData, candleData) {
  if (timeframe === 'Tick') {
    const recent = lineData.slice(-DYNAMIC_TICK_RANGE_POINTS)
      .map((point) => point?.value)
      .filter((value) => Number.isFinite(value));
    if (recent.length === 0) {
      return 0;
    }
    return resolveTrimmedRange(recent);
  }
  const lows = [];
  const highs = [];
  for (const candle of candleData.slice(-DYNAMIC_CANDLE_RANGE_POINTS)) {
    if (Number.isFinite(candle?.low)) {
      lows.push(candle.low);
    }
    if (Number.isFinite(candle?.high)) {
      highs.push(candle.high);
    }
  }
  if (lows.length === 0 || highs.length === 0) {
    return 0;
  }
  const trimmedLow = quantileSorted(lows, DYNAMIC_LOW_QUANTILE);
  const trimmedHigh = quantileSorted(highs, DYNAMIC_HIGH_QUANTILE);
  if (!Number.isFinite(trimmedLow) || !Number.isFinite(trimmedHigh)) {
    return 0;
  }
  return Math.max(0, trimmedHigh - trimmedLow);
}
function resolveTrimmedRange(values) {
  const low = quantileSorted(values, DYNAMIC_LOW_QUANTILE);
  const high = quantileSorted(values, DYNAMIC_HIGH_QUANTILE);
  if (!Number.isFinite(low) || !Number.isFinite(high)) {
    return 0;
  }
  return Math.max(0, high - low);
}
function quantileSorted(values, quantile) {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const clampedQuantile = clamp(quantile, 0, 1);
  const index = (sorted.length - 1) * clampedQuantile;
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  const lowerValue = sorted[lowerIndex];
  const upperValue = sorted[upperIndex];
  if (!Number.isFinite(lowerValue) || !Number.isFinite(upperValue)) {
    return null;
  }
  if (lowerIndex === upperIndex) {
    return lowerValue;
  }
  const ratio = index - lowerIndex;
  return lowerValue + ((upperValue - lowerValue) * ratio);
}
function resolveLegacyAdaptivePriceScaleStep(absolutePrice) {
  if (absolutePrice >= 50000) {
    return 10;
  }
  if (absolutePrice >= 10000) {
    return 5;
  }
  if (absolutePrice >= 1000) {
    return 1;
  }
  if (absolutePrice >= 100) {
    return 0.1;
  }
  if (absolutePrice >= 10) {
    return 0.01;
  }
  if (absolutePrice >= 1) {
    return 0.001;
  }
  if (absolutePrice >= 0.1) {
    return 0.0001;
  }
  return 0.00001;
}
function toNiceStep(value) {
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  const exponent = Math.pow(10, Math.floor(Math.log10(value)));
  const baseCandidates = [1, 2, 2.5, 5, 10];
  const candidates = [
    ...baseCandidates.map((entry) => entry * exponent),
    ...baseCandidates.map((entry) => entry * exponent * 10),
    ...baseCandidates.map((entry) => entry * exponent * 0.1)
  ].filter((candidate) => Number.isFinite(candidate) && candidate > 0);
  let best = candidates[0];
  let bestDistance = Math.abs(best - value);
  for (let index = 1; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const distance = Math.abs(candidate - value);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}
function syncLiveMarker(series, chart, price, time, onLiveMarkerChange) {
  if (!onLiveMarkerChange) {
    return;
  }
  const y = series.priceToCoordinate(price);
  const x = chart.timeScale().timeToCoordinate(time);
  if (x === null || y === null) {
    onLiveMarkerChange(null);
    return;
  }
  onLiveMarkerChange({
    x,
    y,
    price
  });
}

function resolvePriceLineStyle(lineStyle) {
  if (lineStyle === 'dotted') {
    return LineStyle.Dotted;
  }
  if (lineStyle === 'solid') {
    return LineStyle.Solid;
  }
  return LineStyle.Dashed;
}

